import React, { PropsWithChildren, useCallback, useMemo, useRef } from "react";
import {
  DefaultTriggerProvider,
  MessageInputContextProvider,
  useChatContext,
  useChannelStateContext,
  useMessageInputContext,
} from "stream-chat-react";
import type {
  MessageInputContextValue,
  DefaultStreamChatGenerics,
} from "stream-chat-react";
import type { ChannelMemberResponse } from "stream-chat";
import { BOOKS_CHANNEL_CUSTOM_TYPE, TASK_CHANNEL } from "constants/channelType";
import {
  ACCOUNT_MANAGER,
  BOOKKEEPER,
  HEAD_OF_SUPPORT,
  INKLE_CEO,
  INKLE_CTO,
  PREPARER,
  REVIEWER,
} from "constants/chatMemberType";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { MentionUserItem } from "./MentionUserItem";

const TAIL_ROLES = [HEAD_OF_SUPPORT, INKLE_CEO, INKLE_CTO];

const getRoleOrder = (channelCustomType?: string): string[] => {
  if (channelCustomType === BOOKS_CHANNEL_CUSTOM_TYPE) {
    return [ACCOUNT_MANAGER, BOOKKEEPER, ...TAIL_ROLES];
  }
  if (channelCustomType === TASK_CHANNEL) {
    return [ACCOUNT_MANAGER, PREPARER, REVIEWER, ...TAIL_ROLES];
  }
  return [ACCOUNT_MANAGER, ...TAIL_ROLES];
};

const OverrideMentionComponent = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const currentValue = useMessageInputContext("OverrideMentionComponent");
  const { client } = useChatContext("OverrideMentionComponent");
  const { channel } = useChannelStateContext("OverrideMentionComponent");
  const channelCustomType = channel?.data?.custom_type as string | undefined;

  const roleOrder = useMemo(
    () => getRoleOrder(channelCustomType),
    [channelCustomType]
  );

  const sortByAssignedRole = useCallback(
    (data: any[]) =>
      [...data].sort((a, b) => {
        const aRole = a.assigned_roles?.[0] || "";
        const bRole = b.assigned_roles?.[0] || "";
        const aIndex = roleOrder.indexOf(aRole);
        const bIndex = roleOrder.indexOf(bRole);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      }),
    [roleOrder]
  );

  const assignedAgentMemberId = channel?.data
    ?.assigned_agent_member_id as string;
  const assignedPreparerMemberId = channel?.data
    ?.assigned_preparer_member_id as string;
  const assignedReviewerMemberId = channel?.data
    ?.assigned_reviewer_member_id as string;

  const { isAdmin } = useRoleBasedView();
  const allMembers = useMemo<
    Record<string, ChannelMemberResponse<DefaultStreamChatGenerics>>
  >(() => channel?.state?.members ?? {}, [channel?.state?.members]);
  const isBookChannel = channelCustomType === BOOKS_CHANNEL_CUSTOM_TYPE;
  const isFilingChannel = channelCustomType === TASK_CHANNEL;

  const allMembersCache = useRef<
    ChannelMemberResponse<DefaultStreamChatGenerics>[] | null
  >(null);
  const cacheChannelId = useRef<string | null>(null);

  const assignedIdToRoles = useMemo(() => {
    const map = new Map<string, string[]>();

    const addRole = (id: string | undefined, role: string) => {
      if (!id) return;
      const existing = map.get(id) || [];
      existing.push(role);
      map.set(id, existing);
    };

    addRole(assignedAgentMemberId, ACCOUNT_MANAGER);
    addRole(assignedPreparerMemberId, isBookChannel ? BOOKKEEPER : PREPARER);
    if (isFilingChannel) {
      addRole(assignedReviewerMemberId, REVIEWER);
    }

    return map;
  }, [
    assignedAgentMemberId,
    assignedPreparerMemberId,
    assignedReviewerMemberId,
    isBookChannel,
    isFilingChannel,
  ]);

  const customDataProvider = useCallback(
    (
      query: string,
      _text: string,
      onReady: (data: any[], token: string) => void
    ) => {
      const channelId = channel?.id ?? null;

      const fetchAll = async () => {
        if (allMembersCache.current && cacheChannelId.current === channelId) {
          return allMembersCache.current;
        }

        const PAGE_SIZE = 100;
        const initialMembers = Object.values(channel.state.members);

        if (initialMembers.length < PAGE_SIZE) {
          return initialMembers as ChannelMemberResponse<DefaultStreamChatGenerics>[];
        }

        const collected: ChannelMemberResponse<DefaultStreamChatGenerics>[] = [
          ...initialMembers,
        ];
        let offset = PAGE_SIZE;

        while (true) {
          const result = await channel.queryMembers(
            {},
            {},
            { limit: PAGE_SIZE, offset }
          );
          collected.push(...result.members);
          if (result.members.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        }

        allMembersCache.current = collected;
        cacheChannelId.current = channelId;
        return collected;
      };

      fetchAll()
        .then((memberList) => {
          const users = memberList
            .map((m) => {
              const user = m.user;
              if (!user) return null;

              const assignedRoles = assignedIdToRoles.get(user.id);
              if (assignedRoles?.length) {
                return {
                  ...user,
                  role_name: assignedRoles[0],
                  assigned_roles: assignedRoles,
                };
              }

              const roleName = user.role_name as string | undefined;
              if (roleName && TAIL_ROLES.includes(roleName)) {
                return { ...user, assigned_roles: [roleName] };
              }

              return user;
            })
            .filter((user): user is NonNullable<typeof user> => {
              if (!user || user.id === client.userID) return false;
              if (
                !isBookChannel &&
                !isAdmin &&
                user.id === assignedReviewerMemberId
              )
                return false;
              if (!query) return true;

              const lowerQuery = query.toLowerCase();
              const name = (user.name || "").toLowerCase();
              const id = (user.id || "").toLowerCase();

              return name.includes(lowerQuery) || id.includes(lowerQuery);
            });

          const userMap = new Map<string, any>();
          (users as any[]).forEach((user) => {
            const existing = userMap.get(user.id);
            if (
              !existing ||
              (!existing.assigned_roles?.length && user.assigned_roles?.length)
            ) {
              userMap.set(user.id, user);
            }
          });

          onReady(sortByAssignedRole(Array.from(userMap.values())), query);
        })
        .catch(() => {
          onReady(Object.values(allMembers) as any[], query);
        });
    },
    [
      allMembers,
      channel,
      client.userID,
      sortByAssignedRole,
      assignedIdToRoles,
      assignedReviewerMemberId,
      isAdmin,
      isBookChannel,
    ]
  );

  const autocompleteTriggers = currentValue.autocompleteTriggers
    ? {
        ...currentValue.autocompleteTriggers,
        "@": {
          ...currentValue.autocompleteTriggers["@"],
          component: MentionUserItem,
          dataProvider: customDataProvider,
        },
      }
    : currentValue.autocompleteTriggers;

  const newValue = {
    ...currentValue,
    autocompleteTriggers,
  } as MessageInputContextValue;

  return (
    <MessageInputContextProvider value={newValue}>
      {children}
    </MessageInputContextProvider>
  );
};

export const MentionTriggerProvider = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => (
  <DefaultTriggerProvider>
    <OverrideMentionComponent>{children}</OverrideMentionComponent>
  </DefaultTriggerProvider>
);
